import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { FavoriteFilled } from '@wix/wix-ui-icons-common';
import { FC } from 'react';
import LinkWithHref from '@components/Profile/LinkWithHref';
import { Sections } from '@utils/constants';
import { REVIEW_SUMMARY_DATA_HOOKS } from '../ReviewSummary';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import s from './TemplateRating.module.scss';

interface TemplateRatingProps {
  averageRating: number;
  reviewCount: number;
  includeReviewsLink?: boolean;
}

const TemplateRating: FC<TemplateRatingProps> = ({
  averageRating,
  reviewCount,
  includeReviewsLink,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { logBiClick } = useTemplateBi({ is_mobile: false });

  const ratingNumberFormatter = Intl.NumberFormat(language, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format;

  return (
    <>
      {reviewCount > 0 ? (
        <Box
          dataHook={REVIEW_SUMMARY_DATA_HOOKS.templateRatingSection}
          verticalAlign="middle"
        >
          <FavoriteFilled color="rgb(244, 186, 67)" />
          <Box verticalAlign="middle">
            <Text
              size="small"
              weight="thin"
              dataHook={REVIEW_SUMMARY_DATA_HOOKS.averageRating}
            >
              {ratingNumberFormatter(averageRating ?? 0)}
            </Text>
            &nbsp;
            {includeReviewsLink ? (
              <LinkWithHref
                onClick={() =>
                  logBiClick({ clickType: 'reviews - link from rating' })
                }
                href="#reviews"
                smooth
                spy
                to={Sections.REVIEWS}
                className={s.underline}
              >
                <ReviewCount reviewCount={reviewCount} />
              </LinkWithHref>
            ) : (
              <ReviewCount reviewCount={reviewCount} />
            )}
          </Box>
        </Box>
      ) : (
        <Box gap={'2px'} verticalAlign="middle">
          <FavoriteFilled
            color="#00062433"
            style={{
              width: '18px',
              height: '18px',
            }}
          />
          <Text size="small" weight="thin" secondary light>
            {t('marketplace.templates-page.rating-bar.no-reviews')}
          </Text>
        </Box>
      )}
    </>
  );
};

export default TemplateRating;

const ReviewCount: FC<Omit<TemplateRatingProps, 'averageRating'>> = ({
  reviewCount,
}) => {
  const { t } = useTranslation();

  return (
    <Text
      size="small"
      weight="thin"
      dataHook={REVIEW_SUMMARY_DATA_HOOKS.reviewCount}
    >
      {t('marketplace.templates-page.hero-section.review-count', {
        number: reviewCount ?? 0,
      })}
    </Text>
  );
};
